import React, { Fragment, memo, useReducer, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Modal, Text, Checkbox } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import MatchingList from './MatchingList';

import styles from './styles';
const useStyles = createUseStyles(styles, 'MatchingModal');

const initialState = {
    data: null,
    fields: null,
    fieldSchema: null,
    isOpen: false,
    canCreate: false,
    nodeSetData: null,
    refreshCells: null,
    hideMatchedFlields: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                data: action.data,
                fieldSchema: action.fieldSchema,
                fields: action.fields,
                isOpen: true,
                canCreate: action.canCreate,
                matchFieldsNode: action.matchFieldsNode,
                matchFieldsApi: action.matchFieldsApi,
            };
        case 'match': {
            return {
                ...state,
                data: {
                    ...state.data,
                    extraInfo: {
                        ...state.data.extraInfo,
                        [action.field]: action.value,
                    },
                },
            };
        }
        case 'close':
            return { ...state, isOpen: false };
        case 'toggleHideMatchedFlields':
            return { ...state, hideMatchedFlields: !state.hideMatchedFlields };
        default:
            throw new Error('No action provided');
    }
}

const MatchingModal = ({ onConfirm, onRef, matchBy }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    // Modal handlers
    const onConfirmModal = useCallback(() => {
        state.matchFieldsNode.setData(state.data);
        state.matchFieldsApi.refreshCells({ columns: ['nameField', 'extraInfo'], force: true });
        onConfirm && onConfirm(state.data);
        dispatch({ type: 'close' });
    }, [state, onConfirm]);

    const handleOnClose = useCallback(() => {
        dispatch({ type: 'close' });
    }, [dispatch]);

    function open(data, schema, matchFieldsNode, matchFieldsApi) {
        const extraInfo = data?.extraInfo || {};
        const fields = Object.keys(extraInfo);
        const tableSchema = schema?.tableSchema || {};
        const canCreate =
            tableSchema && tableSchema[data.nameField] && tableSchema[data.nameField].extraInfo
                ? tableSchema[data.nameField].extraInfo.canCreate
                : false;

        dispatch({
            type: 'init',
            data,
            fields,
            fieldSchema: tableSchema[data.nameField],
            canCreate,
            matchFieldsNode,
            matchFieldsApi,
        });
    }

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open,
            });
    }, [onRef, dispatch]);

    const onMatch = useCallback((field, value) => {
        const finalValue = value || value === 0 ? value : -1;
        dispatch({ type: 'match', field, value: finalValue });
    }, []);

    const onToggleHideMatchedFlields = useCallback(() => {
        dispatch({ type: 'toggleHideMatchedFlields' });
    }, []);

    const preComponent = useMemo(() => {
        return (
            <Fragment>
                <Text>{t('cfm_label_match_fields_values_list_desc')}</Text>
                <div className={classes.checkboxContainer}>
                    <Checkbox
                        className={classes.checkbox}
                        onChange={onToggleHideMatchedFlields}
                        checked={state.hideMatchedFlields}
                        color="actionMajor"
                    />
                    <Text>{t('cfm_label_match_fields_see_unmatched_values')}</Text>
                </div>
            </Fragment>
        );
    }, [
        classes.checkbox,
        classes.checkboxContainer,
        onToggleHideMatchedFlields,
        state.hideMatchedFlields,
        t,
    ]);

    return (
        <Modal
            title={t('cfm_title_match_fields_values_list', [state.data ? state.data.name : null])}
            size="large"
            isOpen={state.isOpen}
            onRequestClose={handleOnClose}
            onConfirm={onConfirmModal}
            onCancel={handleOnClose}
            confirmText={t('cfm_action_save')}
            cancelText={t('cfm_action_cancel')}
            className={classes.root}
            preComponent={preComponent}
            classes={{ content: classes.content }}
        >
            {state.fieldSchema?.extraInfo && (
                <MatchingList
                    data={state.data}
                    fields={state.fields}
                    fieldTable={state.fieldSchema.extraInfo.table}
                    onMatch={onMatch}
                    canCreate={state.canCreate}
                    hideMatchedFlields={state.hideMatchedFlields}
                    matchBy={matchBy}
                />
            )}
        </Modal>
    );
};

MatchingModal.propTypes = {
    onRef: PropTypes.func,
};

export default memo(MatchingModal);
